/**
 * Workspace members (CLAUDE.md 11.2, 12.1).
 *
 * Three things this has to get right, all of them consequences of the role model
 * rather than of taste:
 *
 * 1. **Roles are explained where they are chosen.** "Moderator" means nothing on its
 *    own; the line under the select says it grants read-only sight of every project
 *    in the workspace, which is a governance fact a firm's owner should meet before
 *    granting it, not after.
 * 2. **Removal says where the work goes.** A member's owned projects pass to the
 *    workspace owner (12.1). An administrator who believes removal deletes their
 *    colleague's projects will hesitate; one who believes it silently leaves them
 *    reachable by the leaver is wrong. So the confirmation states it.
 * 3. **The last owner is protected by the server, and the refusal is shown.** The API
 *    answers 409 `error.workspace.last_owner`. The select is controlled by server
 *    state, so a refused change snaps back on its own, and the message says why --
 *    a control that silently reverts is indistinguishable from one that is broken.
 */
import { useState } from 'react'
import { UserMinus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/api/client'
import {
  useRemoveWorkspaceMember,
  useUpdateWorkspaceMember,
  useWorkspace,
  useWorkspaceMembers,
} from '@/api/workspaces'
import type { WorkspaceMember, WorkspaceRole } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SelectField } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import { formatDateTime } from '@/lib/format'

export const WORKSPACE_ROLES: WorkspaceRole[] = ['owner', 'admin', 'moderator', 'member']

export function MembersPanel() {
  const { t, i18n } = useTranslation()
  const { workspaceId } = useAuth()
  const { toast } = useToast()

  const { data: workspace } = useWorkspace()
  const { data: members, isLoading } = useWorkspaceMembers(workspaceId)
  const updateRole = useUpdateWorkspaceMember(workspaceId)
  const [removing, setRemoving] = useState<WorkspaceMember | null>(null)

  // The server is the authority; this only decides whether to render the controls.
  const canManage = workspace?.capabilities.includes('workspace:manage_members') ?? false

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('members.title')}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <p className="text-sm text-text-muted">{t('members.subtitle')}</p>

        {isLoading && <Skeleton className="h-24 w-full" />}

        {!isLoading && (members?.length ?? 0) === 0 && (
          <p className="text-sm text-text-muted">{t('members.none')}</p>
        )}

        {/* One list rather than a table plus a mobile duplicate: the row wraps into a
            card below `sm`, so there is a single DOM to keep correct (14.3). */}
        <ul className="space-y-2">
          {(members ?? []).map((member) => (
            <li
              key={member.user_id}
              className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-start"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{member.display_name || member.username}</p>
                <p className="truncate text-xs text-text-muted">{member.email}</p>
                {member.joined_at && (
                  <p className="mt-0.5 text-xs text-text-muted">
                    {t('members.joined', {
                      date: formatDateTime(member.joined_at, i18n.language),
                    })}
                  </p>
                )}
              </div>

              <div className="sm:w-52">
                <SelectField
                  label={`${t('members.role')} — ${member.display_name || member.username}`}
                  labelHidden
                  description={t(`members.role_hint.${member.role}`)}
                  disabled={!canManage || updateRole.isPending}
                  // Controlled by server state, so a refusal snaps the control back
                  // on its own -- there is no local draft to leave out of step.
                  value={member.role}
                  onChange={(event) =>
                    updateRole.mutate(
                      { userId: member.user_id, role: event.target.value as WorkspaceRole },
                      {
                        onSuccess: () =>
                          toast({ title: t('members.role_saved'), variant: 'success' }),
                        onError: (caught) =>
                          toast({
                            title: t('common.error'),
                            description: caught instanceof ApiError ? caught.localised : undefined,
                            variant: 'error',
                          }),
                      },
                    )
                  }
                >
                  {WORKSPACE_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {t(`members.role.${role}`)}
                    </option>
                  ))}
                </SelectField>
              </div>

              {canManage && (
                <Button size="sm" variant="ghost" onClick={() => setRemoving(member)}>
                  <UserMinus size={16} aria-hidden />
                  {t('members.remove')}
                </Button>
              )}
            </li>
          ))}
        </ul>

        {!canManage && <p className="text-xs text-text-muted">{t('members.read_only')}</p>}
      </CardBody>

      <RemoveMemberDialog
        member={removing}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
      />
    </Card>
  )
}

function RemoveMemberDialog({
  member,
  onOpenChange,
}: {
  member: WorkspaceMember | null
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { workspaceId } = useAuth()
  const { toast } = useToast()
  const remove = useRemoveWorkspaceMember(workspaceId)
  const [error, setError] = useState<string | null>(null)

  return (
    <Dialog
      open={member !== null}
      onOpenChange={(open) => {
        if (!open) setError(null)
        onOpenChange(open)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('members.remove_title', { name: member?.display_name || member?.username || '' })}
          </DialogTitle>
          {/* Where the work goes is the part an administrator needs before deciding. */}
          <DialogDescription>{t('members.remove_body')}</DialogDescription>
        </DialogHeader>

        {error && (
          <p
            role="alert"
            className="rounded-md bg-[var(--status-fail-bg)] p-3 text-sm text-[var(--status-fail)]"
          >
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="danger"
            loading={remove.isPending}
            onClick={() => {
              if (!member) return
              setError(null)
              remove.mutate(member.user_id, {
                onSuccess: () => {
                  toast({ title: t('members.removed'), variant: 'success' })
                  onOpenChange(false)
                },
                // Inline rather than a toast: the refusal explains why this dialog is
                // still open, so it belongs beside the button that failed.
                onError: (caught) =>
                  setError(caught instanceof ApiError ? caught.localised : String(caught)),
              })
            }}
          >
            {t('members.remove')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
